import { track } from '@dfds-frontend/tracking'
import {
  PromoCzk,
  PromoDkk,
  PromoDollar,
  PromoEuro,
  PromoNok,
  PromoPercent,
  PromoPln,
  PromoPound,
  PromoRon,
  PromoRub,
  PromoSek,
  PromoTry,
} from '@dfds-ui/icons'
import { Button, Headline } from '@dfds-ui/react-components'
import { media, theme } from '@dfds-ui/theme'
import { Text } from '@dfds-ui/typography'
import { css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { graphql } from 'gatsby'
import { createContext, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import AutoLink from '../EntryLink/AutoLink'
import { useLocalizedLinkTrackingData } from '../EntryLink/LocalizedLink'
import TravelSearchSkeleton from '../Skeleton/TravelSearchSkeleton'
import TravelSearchOfferSkeleton from '../Skeleton/TravelSearchOfferSkeleton'
import HeroImage from './Image'
import { useLocaleContext } from '../LocaleContext'

const PaxHeroBlock = styled.div`
  position: relative;
  display: block;
  height: auto;
  min-height: 315px;
  margin-bottom: 40px;

  ${media.greaterThan('m')`
    min-height: 505px;
  `}

  ${media.greaterThan('l')`
    display: flex;
    flex-direction: column;
    min-height: 419px;
    max-height: 603px;
  `}
`

const PaxHeroBlockContainer = styled.div`
  display: flex;
  background-color: ${(props) => props.theme['color-background']};
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;

  ${media.greaterThan('l')`
    flex-grow: 1;
    background-color: transparent;
    z-index: 1;
  `}
`

const PaxHeroBlockInfo = styled.div`
  padding: 20px;

  ${media.greaterThan('l')`
    max-width: 45%;
  `}
`

const PaxHeroBlockText = styled.div`
  & > div,
  & > h1 {
    font-size: 30px;
    line-height: 36px;
    font-weight: 300;
    color: ${(props) => props.theme['color-text']};
    margin: 0;

    ${media.greaterThan('m')`
      font-size: 50px;
      line-height: 60px;
    `}
  }
`

const PaxHeroBlockSpecialoffer = styled.div`
  position: relative;
  margin: 25px 0 0 -20px;
  padding-left: 20px;
  max-width: 245px;
  font-weight: 700;
  line-height: 25px;
  color: ${theme.colors.text.primary.primary};
  background-color: ${theme.colors.surface.primary};

  &:after {
    position: absolute;
    top: 0;
    right: -12px;
    content: '';
    width: 0;
    height: 0;
    border-top: 25px solid ${theme.colors.surface.primary};
    border-right: 13px solid transparent;
  }
`

const SpecialOfferIcon = (props) => {
  const iconTypes = {
    'pax-percentage-sign-no-theme': <PromoPercent {...props} />,
    'pax-euro-sign-no-theme': <PromoEuro {...props} />,
    'pax-gbp-sign-no-theme': <PromoPound {...props} />,
    'pax-dkk-sign-no-theme': <PromoDkk {...props} />,
    'pax-dollar-sign-no-theme': <PromoDollar {...props} />,
    'pax-czk-sign-no-theme': <PromoCzk {...props} />,
    'pax-nok-sign-no-theme': <PromoNok {...props} />,
    'pax-pln-sign-no-theme': <PromoPln {...props} />,
    'pax-ron-sign-no-theme': <PromoRon {...props} />,
    'pax-rub-sign-no-theme': <PromoRub {...props} />,
    'pax-sek-sign-no-theme': <PromoSek {...props} />,
    'pax-try-sign-no-theme': <PromoTry {...props} />,
  }
  return iconTypes[props.icon]
}

export const HeroContext = createContext(undefined)

export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const getVercelHost = (currentVersion) => {
  const { host } = window.location
  const newTSEnabled =
    window?._vwo_exp &&
    window._vwo_exp[currentVersion]?.combination_chosen === '3'

  if (host === 'www.dfds.com')
    return newTSEnabled
      ? 'https://travel-search-ab-prod.dfds-pax-web.com'
      : 'https://travel-search-prod.dfds-pax-web.com'
  else if (host === 'www.accept.dfds.com')
    return newTSEnabled
      ? 'https://travel-search-ab-uat.dfds-pax-web.com'
      : 'https://travel-search-uat.dfds-pax-web.com'
  else if (host === 'www.test.dfds.com')
    return newTSEnabled
      ? 'https://travel-search-ab-test.dfds-pax-web.com'
      : 'https://travel-search-test.dfds-pax-web.com'
  else if (
    // this is needed for preprod e2e/cypress test that checks if travel-search is working in prod before deploying to prod
    host.includes('dfds-gatsby-bucket-preprod.s3.eu-central-1.amazonaws.com')
  )
    return 'https://travel-search-prod.dfds-pax-web.com'
  else
    return newTSEnabled
      ? 'https://travel-search-ab-dev.dfds-pax-web.com'
      : 'https://travel-search-dev.dfds-pax-web.com'
}

const loadFromVercelAndInjectScript = async (currentVersion) => {
  const host = getVercelHost(currentVersion)
  const path = `${host}/.vite/manifest.json`
  const response = await fetch(path)
  const manifest = await response.json()
  await new Promise((resolve) => {
    const script = document.createElement('script')
    script.src = `${host}/${manifest['index.html']['file']}`
    script.type = 'module'
    script.id = 'travel-search-script'
    script.async = true
    script.fetchpriority = 'high'
    script.onload = resolve
    document.querySelector('body')?.appendChild(script)
    const stylesheet = document.createElement('link')
    stylesheet.rel = 'stylesheet'
    stylesheet.href = `${host}/${manifest['index.html']['css'][0]}`
    document.querySelector('head')?.appendChild(stylesheet)
  })

  let travelSearchLoaded = document.travelSearchCreate !== null

  while (!travelSearchLoaded) {
    await delay(500)
    travelSearchLoaded = document.travelSearchCreate !== null
  }
}

const Hero = (props) => {
  const {
    image,
    price,
    showFromPrice,
    category,
    categoryOverride,
    text,
    makeTitleH1,
    currency,
    placeCurrency,
    miniBookReference,
    pricePostText,
    specialOfferInfo,
    specialOfferIcon,
    ctaButton,
    description,
  } = props

  const { t } = useTranslation()
  const themeContext = useTheme()
  const { locale } = useLocaleContext()
  const heroRef = useRef()
  const ctaUrl = ctaButton?.destination?.slug
    ? { url: ctaButton?.destination, isInternal: true }
    : ctaButton?.url
      ? { url: ctaButton.url, isInternal: false }
      : null

  const { getLocalizedLinkTrackingData } = useLocalizedLinkTrackingData()

  const trackingData = getLocalizedLinkTrackingData(ctaButton)

  const hasOffer =
    miniBookReference?.productListSectionsCollection !== undefined

  useEffect(() => {
    ;(async () => {
      if (typeof window !== 'undefined' && miniBookReference && locale) {
        const maxLoops = 6
        const currentVersion = 485

        for (let i = 0; i < maxLoops; i++) {
          if (locale !== 'en-GB') break
          if (
            window._vwo_exp &&
            window._vwo_exp[currentVersion]?.status !== 'RUNNING'
          ) {
            break
          }
          if (
            window._vwo_exp !== undefined &&
            window?._vwo_exp[currentVersion]
          ) {
            if (window._vwo_exp[currentVersion]?.combination_chosen) break
          }
          await delay(500)
        }

        window.sessionStorage.setItem('travelSearchRefs', {
          minibookId: miniBookReference.sys.id,
        })
        // To prevent script being added multiple times
        const travelSearchScriptLoaded =
          document.getElementById('travel-search-script') !== null

        if (!travelSearchScriptLoaded) {
          await loadFromVercelAndInjectScript(currentVersion)
        }

        const travelSearchLoaded =
          document.getElementById('travel-search') !== null

        if (!travelSearchLoaded) {
          const hasOffer =
            miniBookReference?.productListSectionsCollection !== undefined
              ? true
              : false
          document.travelSearchCreate(hasOffer)
        }
      }
    })()
  }, [miniBookReference, locale])

  if (image) {
    return (
      <HeroContext.Provider>
        <PaxHeroBlock ref={heroRef}>
          <HeroImage image={image} />
          <PaxHeroBlockContainer>
            <PaxHeroBlockInfo>
              {category && (
                <div
                  css={css`
                    margin-bottom: 10px;
                    font-weight: 700;
                    color: ${themeContext['color-text']};
                  `}
                >
                  {categoryOverride || category}
                </div>
              )}

              {text && (
                <PaxHeroBlockText>
                  <Headline
                    as={makeTitleH1 !== false ? 'h1' : 'div'}
                    css={css`
                      color: ${themeContext['color-text']};
                    `}
                  >
                    {text}
                  </Headline>
                  {description && (
                    <Text
                      styledAs={'subHeadline'}
                      css={css`
                        color: ${themeContext['color-text']};
                        margin-top: 1rem !important;
                      `}
                    >
                      {description}
                    </Text>
                  )}
                  {!miniBookReference && ctaButton && (
                    <div
                      css={css`
                        margin-top: 15px !important;

                        ${media.greaterThan('l')`
                          position: absolute;
                          bottom: 20px;
                          left: 20px;
                          margin-top: 0;
                        `}
                      `}
                    >
                      <Button
                        as={AutoLink}
                        destination={ctaUrl.isInternal ? ctaUrl.url : undefined}
                        url={ctaUrl.isInternal ? undefined : ctaUrl.url}
                        target={ctaUrl.isInternal ? undefined : false}
                        onClick={() =>
                          ctaButton?.ctaButtonTracking && track(trackingData)
                        }
                        css={css`
                          color: ${theme.colors.text.light.primary};
                          &:hover,
                          &:visited {
                            color: ${theme.colors.text.light.primary};
                          }
                        `}
                      >
                        {props.ctaButton.linkText}
                      </Button>
                    </div>
                  )}
                </PaxHeroBlockText>
              )}

              {price && (
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    color: ${themeContext['color-text']};
                    padding: 20px 0 0;

                    ${media.greaterThan('l')`
                      flex-direction: row;
                    `}
                  `}
                >
                  <div
                    css={css`
                      display: block;
                      font-size: 14px;
                      line-height: 17px;
                      font-weight: 300;
                      padding-bottom: 5px;

                      ${media.greaterThan('l')`
                        display: none;
                      `}
                    `}
                  >
                    {showFromPrice !== false
                      ? currency === '%'
                        ? t('GENERAL-SAVE')
                        : t('GENERAL-FROM')
                      : '\u00A0'}
                  </div>
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                    `}
                  >
                    <div
                      css={css`
                        align-self: flex-end;

                        ${media.greaterThan('l')`
                          align-self: flex-start;
                          text-align: right;
                          margin: 0 4px 0 0;
                        `}
                      `}
                    >
                      <div
                        css={css`
                          display: none;
                          font-size: 14px;
                          line-height: 17px;
                          font-weight: 300;
                          padding-bottom: 5px;

                          ${media.greaterThan('l')`
                            display: block;  
                            padding-bottom: 0;
                          `}
                        `}
                      >
                        {showFromPrice !== false
                          ? currency === '%'
                            ? t('GENERAL-SAVE')
                            : t('GENERAL-FROM')
                          : '\u00A0'}
                      </div>
                      {currency && placeCurrency !== false && (
                        <div
                          css={css`
                            font-size: 30px;
                            line-height: 34px;
                            font-weight: 300;
                            text-transform: uppercase;
                            margin: 0 4px 0 0;

                            ${media.greaterThan('l')`
                              line-height: 26px;
                              margin: 0;
                            `}
                          `}
                        >
                          {currency}
                        </div>
                      )}
                    </div>
                    <div
                      css={css`
                        font-size: 40px;
                        line-height: 40px;
                        font-weight: 700;

                        ${media.greaterThan('l')`
                          font-size: 60px;
                          line-height: 44px;
                        `}
                      `}
                    >
                      {price.toFixed(2).replace(/[.,]00$/, '')}
                    </div>
                    {currency && placeCurrency === false && (
                      <div
                        css={css`
                          align-self: flex-end;
                        `}
                      >
                        <div
                          css={css`
                            font-size: 30px;
                            line-height: 34px;
                            font-weight: 300;
                            text-transform: uppercase;
                            margin: 0 4px 0 4px;

                            ${media.greaterThan('l')`
                              line-height: 26px;
                            `}
                          `}
                        >
                          {currency}
                        </div>
                      </div>
                    )}
                  </div>
                  {pricePostText && (
                    <div
                      css={css`
                        font-size: 14px;
                        line-height: 17px;
                        font-weight: 300;

                        ${media.greaterThan('l')`
                          align-self: flex-end;
                          margin: 0 0 0 4px;
                        `}
                      `}
                    >
                      {pricePostText}
                    </div>
                  )}
                </div>
              )}

              {specialOfferInfo && (
                <PaxHeroBlockSpecialoffer>
                  {specialOfferIcon && (
                    <SpecialOfferIcon
                      icon={specialOfferIcon}
                      css={css`
                        width: 16px;
                        height: 16px;
                        margin: -1px 5px 0 0;
                        position: relative;
                      `}
                    />
                  )}
                  {specialOfferInfo}
                </PaxHeroBlockSpecialoffer>
              )}
            </PaxHeroBlockInfo>

            {miniBookReference && (
              <div
                id="travel-search-dotcom-container"
                css={css`
                  width: 100%;
                  background: transparent;
                  display: flex;

                  ${media.greaterThan('l')`
                    padding: 16px;
                  `}
                `}
              >
                <div
                  css={css`
                    margin-top: auto;
                    width: 100%;
                  `}
                  id="travel-search-root"
                >
                  {hasOffer ? (
                    <TravelSearchOfferSkeleton />
                  ) : (
                    <TravelSearchSkeleton />
                  )}
                </div>
              </div>
            )}
          </PaxHeroBlockContainer>
        </PaxHeroBlock>
      </HeroContext.Provider>
    )
  }
  return (
    <div
      css={css`
        padding: 20px 20px 0;
        ${media.greaterThan('l')`
          padding: 80px 0 0;
        `}
      `}
    >
      <Headline>{text}</Headline>
    </div>
  )
}

export const query = graphql`
  fragment PaxHeroContent on contentful_PaxHeroContent {
    image {
      ...Asset
    }
    category
    text
    makeTitleH1
    price
    showFromPrice
    currency
    placeCurrency
    specialOfferInfo
    specialOfferIcon
    pricePostText
    ctaButton {
      ...ItemLink
    }
    description
  }

  fragment Minibook on contentful_MiniBook {
    __typename
    sys {
      id
    }
  }

  fragment MinibookProductList on contentful_MinibookProductList {
    __typename
    sys {
      id
    }
    displayRoute {
      name
    }
    optionalOfferTitle
    productListSectionsCollection(limit: 3) {
      items {
        productSectionTitle
        minibookProductsCollection(limit: 4) {
          items {
            title
            productDetails
            offerDetails
            minibookContentReference {
              sys {
                id
              }
            }
          }
        }
      }
    }
  }
`

export default Hero
